import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import {
  ExternalLink,
  ImagePlus,
  Images,
  Loader2,
  Mail,
  Phone,
  Plus,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { WebsiteCampaign } from '@pigeons/shared-website-media';
import { resolve_website_asset_url, get_campaign_public_url } from '../lib/site_config';
import { websiteContentService } from '../services/websiteContentService';
import { campaignLeadService, CampaignLead } from '../services/campaignLeadService';
import WebsiteImageLibraryModal from '../components/admin/WebsiteImageLibraryModal';

type campaign_image_library_target = { kind: 'hero'; id: string } | { kind: 'gallery'; id: string };

const get_error_message = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const benefit_keys = [1, 2, 3, 4] as const;

export default function CampaignsAdmin() {
  const { t } = useTranslation('admin');

  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<WebsiteCampaign[]>([]);
  const [savingKeys, setSavingKeys] = useState<Record<string, boolean>>({});

  const [imageLibraryOpen, setImageLibraryOpen] = useState(false);
  const [imageLibraryTarget, setImageLibraryTarget] =
    useState<campaign_image_library_target | null>(null);

  const [leadsCampaignId, setLeadsCampaignId] = useState<string | null>(null);
  const [allLeads, setAllLeads] = useState<CampaignLead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(true);

  const setSaving = (token: string, value: boolean) => {
    setSavingKeys(prev => ({ ...prev, [token]: value }));
  };

  const refreshCampaigns = async () => {
    setLoading(true);
    try {
      const next = await websiteContentService.listCampaigns();
      setCampaigns(next);
    } finally {
      setLoading(false);
    }
  };

  const refreshLeads = async () => {
    setLeadsLoading(true);
    try {
      const items = await campaignLeadService.listLeads();
      setAllLeads(items);
    } catch (error) {
      toast.error(
        `${t('campaigns.leads_error')}: ${get_error_message(error, t('campaigns.unknown_error'))}`
      );
    } finally {
      setLeadsLoading(false);
    }
  };

  useEffect(() => {
    void refreshCampaigns();
    void refreshLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-once load, matches refreshContent pattern elsewhere in admin
  }, []);

  const leadCountsByCampaignId = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const lead of allLeads) {
      counts[lead.campaign_id] = (counts[lead.campaign_id] ?? 0) + 1;
    }
    return counts;
  }, [allLeads]);

  const leadsForSelectedCampaign = useMemo(
    () => allLeads.filter(lead => lead.campaign_id === leadsCampaignId),
    [allLeads, leadsCampaignId]
  );

  const updateCampaignField = <K extends keyof WebsiteCampaign>(
    id: string,
    field: K,
    value: WebsiteCampaign[K]
  ) => {
    setCampaigns(prev => prev.map(item => (item.id === id ? { ...item, [field]: value } : item)));
  };

  const handleAddCampaign = async () => {
    const token = 'campaign:add';
    setSaving(token, true);
    try {
      const baseSlug = `new-campaign-${Date.now().toString(36)}`;
      const item = await websiteContentService.createCampaign(baseSlug);
      setCampaigns(prev => [item, ...prev]);
      toast.success(t('campaigns.save_success'));
    } catch (error) {
      toast.error(
        `${t('campaigns.create_error')}: ${get_error_message(error, t('campaigns.unknown_error'))}`
      );
    } finally {
      setSaving(token, false);
    }
  };

  const saveCampaign = async (item: WebsiteCampaign) => {
    const token = `campaign:${item.id}`;
    setSaving(token, true);
    try {
      const updated = await websiteContentService.updateCampaign(item.id, {
        slug: slugify(item.slug),
        published: item.published,
        profession: item.profession,
        badge_fr: item.badge_fr,
        badge_en: item.badge_en,
        headline_fr: item.headline_fr,
        headline_en: item.headline_en,
        subheadline_fr: item.subheadline_fr,
        subheadline_en: item.subheadline_en,
        hero_image_url: item.hero_image_url,
        hero_image_alt_fr: item.hero_image_alt_fr,
        hero_image_alt_en: item.hero_image_alt_en,
        benefit_1_fr: item.benefit_1_fr,
        benefit_1_en: item.benefit_1_en,
        benefit_2_fr: item.benefit_2_fr,
        benefit_2_en: item.benefit_2_en,
        benefit_3_fr: item.benefit_3_fr,
        benefit_3_en: item.benefit_3_en,
        benefit_4_fr: item.benefit_4_fr,
        benefit_4_en: item.benefit_4_en,
        gallery_image_urls: item.gallery_image_urls,
        cta_text_fr: item.cta_text_fr,
        cta_text_en: item.cta_text_en,
      });
      setCampaigns(prev => prev.map(existing => (existing.id === item.id ? updated : existing)));
      toast.success(t('campaigns.save_success'));
    } catch (error) {
      toast.error(
        `${t('campaigns.save_error')}: ${get_error_message(error, t('campaigns.unknown_error'))}`
      );
    } finally {
      setSaving(token, false);
    }
  };

  const deleteCampaign = async (id: string) => {
    if (!window.confirm(t('campaigns.confirm_delete_item'))) {
      return;
    }

    const token = `campaign-delete:${id}`;
    setSaving(token, true);
    try {
      await websiteContentService.deleteCampaign(id);
      setCampaigns(prev => prev.filter(item => item.id !== id));
      if (leadsCampaignId === id) {
        setLeadsCampaignId(null);
      }
    } catch (error) {
      toast.error(
        `${t('campaigns.delete_error')}: ${get_error_message(error, t('campaigns.unknown_error'))}`
      );
    } finally {
      setSaving(token, false);
    }
  };

  const uploadCampaignImage = async (
    token: string,
    event: ChangeEvent<HTMLInputElement>,
    onUploaded: (imageUrl: string) => void
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setSaving(token, true);
    try {
      const imageUrl = await websiteContentService.uploadContentImage('campaigns', file);
      onUploaded(imageUrl);
      toast.success(t('campaigns.image_uploaded'));
    } catch (error) {
      toast.error(
        `${t('campaigns.image_upload_error')}: ${get_error_message(error, t('campaigns.unknown_error'))}`
      );
    } finally {
      setSaving(token, false);
      event.target.value = '';
    }
  };

  const openImageLibrary = (target: campaign_image_library_target) => {
    setImageLibraryTarget(target);
    setImageLibraryOpen(true);
  };

  const handleImageLibraryPick = (url: string) => {
    setImageLibraryOpen(false);
    const target = imageLibraryTarget;
    setImageLibraryTarget(null);
    if (!target) {
      return;
    }

    if (target.kind === 'hero') {
      updateCampaignField(target.id, 'hero_image_url', url);
    } else {
      setCampaigns(prev =>
        prev.map(item =>
          item.id === target.id
            ? { ...item, gallery_image_urls: [...item.gallery_image_urls, url] }
            : item
        )
      );
    }
  };

  const removeGalleryImage = (campaignId: string, index: number) => {
    setCampaigns(prev =>
      prev.map(item =>
        item.id === campaignId
          ? {
              ...item,
              gallery_image_urls: item.gallery_image_urls.filter((_, i) => i !== index),
            }
          : item
      )
    );
  };

  const toggleLeads = (campaignId: string) => {
    setLeadsCampaignId(prev => (prev === campaignId ? null : campaignId));
  };

  return (
    <div className="flex flex-col gap-8 px-4 py-6 md:px-6 lg:px-10">
      <div className="flex flex-col gap-3">
        <h1 className="text-4xl font-bold text-secondary">{t('campaigns.title')}</h1>
        <p className="max-w-4xl text-base text-gray-500">{t('campaigns.subtitle')}</p>
      </div>

      {loading ? (
        <div className="rounded-3xl border border-gray-200 bg-white px-6 py-10 text-sm text-gray-500 shadow-sm">
          {t('loading')}
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          <section className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div />
              <button
                type="button"
                onClick={() => void handleAddCampaign()}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
                disabled={Boolean(savingKeys['campaign:add'])}
              >
                {savingKeys['campaign:add'] ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {t('campaigns.add_item')}
              </button>
            </div>

            <div className="flex flex-col gap-5">
              {campaigns.map(item => {
                const token = `campaign:${item.id}`;
                const uploadToken = `campaign-upload:${item.id}`;
                const galleryUploadToken = `campaign-gallery-upload:${item.id}`;
                const deleteToken = `campaign-delete:${item.id}`;
                const isSaving =
                  Boolean(savingKeys[token]) ||
                  Boolean(savingKeys[uploadToken]) ||
                  Boolean(savingKeys[galleryUploadToken]) ||
                  Boolean(savingKeys[deleteToken]);

                return (
                  <article
                    key={item.id}
                    className="rounded-3xl border border-gray-200 bg-gray-50/60 p-4"
                  >
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            item.published
                              ? 'bg-primary/10 text-primary'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {item.published ? t('campaigns.published') : t('campaigns.unpublished')}
                        </span>
                        <a
                          href={get_campaign_public_url(item.slug)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                        >
                          {t('campaigns.preview_url')}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleLeads(item.id)}
                          className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-800 hover:bg-gray-50"
                        >
                          <Users className="h-4 w-4" />
                          {t('campaigns.view_leads')}
                          <span
                            className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
                              (leadCountsByCampaignId[item.id] ?? 0) > 0
                                ? 'bg-primary/10 text-primary'
                                : 'bg-gray-100 text-gray-400'
                            }`}
                          >
                            {leadsLoading ? '…' : (leadCountsByCampaignId[item.id] ?? 0)}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => void deleteCampaign(item.id)}
                          disabled={isSaving}
                          className="rounded-full border border-red-200 p-2 text-red-600 hover:bg-red-50 disabled:opacity-40"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-3">
                      <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                        <span>{t('campaigns.fields.slug')}</span>
                        <input
                          type="text"
                          value={item.slug}
                          onChange={event =>
                            updateCampaignField(item.id, 'slug', event.target.value)
                          }
                          className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm"
                        />
                        <span className="text-xs text-gray-400">
                          {t('campaigns.fields.slug_hint')}
                        </span>
                      </label>
                      <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                        <span>{t('campaigns.fields.profession')}</span>
                        <input
                          type="text"
                          value={item.profession}
                          onChange={event =>
                            updateCampaignField(item.id, 'profession', event.target.value)
                          }
                          className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm"
                        />
                      </label>
                    </div>

                    <label className="mt-3 flex items-center gap-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        checked={item.published}
                        onChange={event =>
                          updateCampaignField(item.id, 'published', event.target.checked)
                        }
                      />
                      {t('campaigns.published')}
                    </label>

                    <div className="mt-4 grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
                      <div className="flex flex-col gap-3">
                        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
                          {item.hero_image_url ? (
                            <img
                              src={resolve_website_asset_url(item.hero_image_url)}
                              alt={item.hero_image_alt_en || ''}
                              className="h-44 w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-44 items-center justify-center bg-gray-100 text-sm text-gray-400">
                              {t('campaigns.no_image')}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90">
                            <ImagePlus className="h-4 w-4" />
                            {t('campaigns.upload')}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={event =>
                                void uploadCampaignImage(uploadToken, event, imageUrl =>
                                  updateCampaignField(item.id, 'hero_image_url', imageUrl)
                                )
                              }
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => openImageLibrary({ kind: 'hero', id: item.id })}
                            disabled={isSaving}
                            className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-40"
                          >
                            <Images className="h-4 w-4" />
                            {t('campaigns.choose_from_library')}
                          </button>
                        </div>
                        <div className="grid gap-2">
                          <input
                            type="text"
                            placeholder={t('campaigns.fields.hero_image_alt_fr')}
                            value={item.hero_image_alt_fr || ''}
                            onChange={event =>
                              updateCampaignField(
                                item.id,
                                'hero_image_alt_fr',
                                event.target.value || null
                              )
                            }
                            className="rounded-2xl border border-gray-300 bg-white px-4 py-2 text-sm"
                          />
                          <input
                            type="text"
                            placeholder={t('campaigns.fields.hero_image_alt_en')}
                            value={item.hero_image_alt_en || ''}
                            onChange={event =>
                              updateCampaignField(
                                item.id,
                                'hero_image_alt_en',
                                event.target.value || null
                              )
                            }
                            className="rounded-2xl border border-gray-300 bg-white px-4 py-2 text-sm"
                          />
                        </div>
                      </div>

                      <div className="grid gap-3">
                        <div className="grid gap-3 md:grid-cols-2">
                          <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                            <span>{t('campaigns.fields.badge_fr')}</span>
                            <input
                              type="text"
                              value={item.badge_fr || ''}
                              onChange={event =>
                                updateCampaignField(item.id, 'badge_fr', event.target.value || null)
                              }
                              className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm"
                            />
                          </label>
                          <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                            <span>{t('campaigns.fields.badge_en')}</span>
                            <input
                              type="text"
                              value={item.badge_en || ''}
                              onChange={event =>
                                updateCampaignField(item.id, 'badge_en', event.target.value || null)
                              }
                              className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm"
                            />
                          </label>
                        </div>

                        <div className="grid gap-3 md:grid-cols-2">
                          <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                            <span>{t('campaigns.fields.headline_fr')}</span>
                            <textarea
                              value={item.headline_fr}
                              onChange={event =>
                                updateCampaignField(item.id, 'headline_fr', event.target.value)
                              }
                              rows={2}
                              className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm"
                            />
                          </label>
                          <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                            <span>{t('campaigns.fields.headline_en')}</span>
                            <textarea
                              value={item.headline_en}
                              onChange={event =>
                                updateCampaignField(item.id, 'headline_en', event.target.value)
                              }
                              rows={2}
                              className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm"
                            />
                          </label>
                        </div>

                        <div className="grid gap-3 md:grid-cols-2">
                          <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                            <span>{t('campaigns.fields.subheadline_fr')}</span>
                            <textarea
                              value={item.subheadline_fr || ''}
                              onChange={event =>
                                updateCampaignField(
                                  item.id,
                                  'subheadline_fr',
                                  event.target.value || null
                                )
                              }
                              rows={2}
                              className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm"
                            />
                          </label>
                          <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                            <span>{t('campaigns.fields.subheadline_en')}</span>
                            <textarea
                              value={item.subheadline_en || ''}
                              onChange={event =>
                                updateCampaignField(
                                  item.id,
                                  'subheadline_en',
                                  event.target.value || null
                                )
                              }
                              rows={2}
                              className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm"
                            />
                          </label>
                        </div>

                        {benefit_keys.map(order => {
                          const frField = `benefit_${order}_fr` as keyof WebsiteCampaign;
                          const enField = `benefit_${order}_en` as keyof WebsiteCampaign;
                          return (
                            <div key={order} className="grid gap-3 md:grid-cols-2">
                              <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                                <span>{t('campaigns.fields.benefit_fr', { order })}</span>
                                <input
                                  type="text"
                                  value={(item[frField] as string | null) || ''}
                                  onChange={event =>
                                    updateCampaignField(
                                      item.id,
                                      frField,
                                      (event.target.value ||
                                        null) as WebsiteCampaign[typeof frField]
                                    )
                                  }
                                  className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm"
                                />
                              </label>
                              <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                                <span>{t('campaigns.fields.benefit_en', { order })}</span>
                                <input
                                  type="text"
                                  value={(item[enField] as string | null) || ''}
                                  onChange={event =>
                                    updateCampaignField(
                                      item.id,
                                      enField,
                                      (event.target.value ||
                                        null) as WebsiteCampaign[typeof enField]
                                    )
                                  }
                                  className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm"
                                />
                              </label>
                            </div>
                          );
                        })}

                        <div className="grid gap-3 md:grid-cols-2">
                          <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                            <span>{t('campaigns.fields.cta_text_fr')}</span>
                            <input
                              type="text"
                              value={item.cta_text_fr || ''}
                              onChange={event =>
                                updateCampaignField(
                                  item.id,
                                  'cta_text_fr',
                                  event.target.value || null
                                )
                              }
                              className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm"
                            />
                          </label>
                          <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                            <span>{t('campaigns.fields.cta_text_en')}</span>
                            <input
                              type="text"
                              value={item.cta_text_en || ''}
                              onChange={event =>
                                updateCampaignField(
                                  item.id,
                                  'cta_text_en',
                                  event.target.value || null
                                )
                              }
                              className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm"
                            />
                          </label>
                        </div>

                        <div className="flex flex-col gap-2">
                          <span className="text-sm font-medium text-gray-700">
                            {t('campaigns.fields.gallery')}
                          </span>
                          <div className="flex flex-wrap gap-3">
                            {item.gallery_image_urls.map((url, index) => (
                              <div
                                key={`${url}-${index}`}
                                className="relative h-20 w-20 overflow-hidden rounded-2xl border border-gray-200"
                              >
                                <img
                                  src={resolve_website_asset_url(url)}
                                  alt=""
                                  className="h-full w-full object-cover"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeGalleryImage(item.id, index)}
                                  className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-gray-700 hover:bg-white"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50">
                              <ImagePlus className="h-4 w-4" />
                              {t('campaigns.upload')}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={event =>
                                  void uploadCampaignImage(galleryUploadToken, event, imageUrl =>
                                    setCampaigns(prev =>
                                      prev.map(existing =>
                                        existing.id === item.id
                                          ? {
                                              ...existing,
                                              gallery_image_urls: [
                                                ...existing.gallery_image_urls,
                                                imageUrl,
                                              ],
                                            }
                                          : existing
                                      )
                                    )
                                  )
                                }
                              />
                            </label>
                            <button
                              type="button"
                              onClick={() => openImageLibrary({ kind: 'gallery', id: item.id })}
                              disabled={isSaving}
                              className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-40"
                            >
                              <Images className="h-4 w-4" />
                              {t('campaigns.choose_from_library')}
                            </button>
                          </div>
                        </div>

                        <div className="flex justify-end pt-1">
                          <button
                            type="button"
                            onClick={() => void saveCampaign(item)}
                            disabled={isSaving}
                            className="rounded-full bg-secondary px-5 py-2.5 text-sm font-semibold text-white hover:bg-secondary/90 disabled:opacity-50"
                          >
                            {isSaving ? t('campaigns.saving') : t('campaigns.save')}
                          </button>
                        </div>
                      </div>
                    </div>

                    {leadsCampaignId === item.id && (
                      <div className="mt-5 rounded-3xl border border-gray-200 bg-white p-4">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <h4 className="text-sm font-semibold text-gray-900">
                            {t('campaigns.leads_title')}
                          </h4>
                          <button
                            type="button"
                            onClick={() => void refreshLeads()}
                            disabled={leadsLoading}
                            className="text-xs font-semibold text-primary hover:underline disabled:opacity-50"
                          >
                            {t('campaigns.refresh')}
                          </button>
                        </div>
                        {leadsLoading ? (
                          <div className="text-sm text-gray-500">{t('loading')}</div>
                        ) : leadsForSelectedCampaign.length === 0 ? (
                          <div className="text-sm text-gray-500">{t('campaigns.leads_empty')}</div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                              <thead>
                                <tr className="text-xs uppercase text-gray-400">
                                  <th className="pb-2 pr-4">{t('campaigns.leads_fields.name')}</th>
                                  <th className="pb-2 pr-4">{t('campaigns.leads_fields.email')}</th>
                                  <th className="pb-2 pr-4">{t('campaigns.leads_fields.phone')}</th>
                                  <th className="pb-2 pr-4">
                                    {t('campaigns.leads_fields.created_at')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {leadsForSelectedCampaign.map(lead => (
                                  <tr key={lead.id} className="border-t border-gray-100">
                                    <td className="py-2 pr-4 font-medium text-gray-900">
                                      {lead.name}
                                    </td>
                                    <td className="py-2 pr-4">
                                      <a
                                        href={`mailto:${lead.email}`}
                                        className="inline-flex items-center gap-1 text-primary hover:underline"
                                      >
                                        <Mail className="h-3.5 w-3.5" />
                                        {lead.email}
                                      </a>
                                    </td>
                                    <td className="py-2 pr-4">
                                      {lead.phone ? (
                                        <a
                                          href={`tel:${lead.phone}`}
                                          className="inline-flex items-center gap-1 text-primary hover:underline"
                                        >
                                          <Phone className="h-3.5 w-3.5" />
                                          {lead.phone}
                                        </a>
                                      ) : (
                                        '—'
                                      )}
                                    </td>
                                    <td className="py-2 pr-4">
                                      {new Date(lead.created_at).toLocaleString()}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      )}

      <WebsiteImageLibraryModal
        isOpen={imageLibraryOpen}
        onClose={() => {
          setImageLibraryOpen(false);
          setImageLibraryTarget(null);
        }}
        onPick={handleImageLibraryPick}
      />
    </div>
  );
}
